import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouteError } from 'react-router-dom';
import _ from 'lodash';

import { Box, FlexBox } from '@sternum/styled-system';
import { Button } from '@sternum/ui';
import { SternumLogoWithTextIcon } from '@sternum/web/icons';
import { namespaces } from '@sternum/web/services/i18n';

import { Colors } from '../theme/constants';
import { ThemeProvider } from '../theme/main';

import { BottomLines, TopLines } from './AbstractLines.component';
import { Code401 } from './Code401.component';
import { Code403 } from './Code403.component';
import { Code404 } from './Code404.component';
import { Code500 } from './Code500.component';
import { ErrorReason } from './ErrorScreen.types';
import { AppError, AppErrorCause } from './throwAppError.util';

import { CodeBox, Container, Content, Description, LinesBox, LogoBox, Title } from './ErrorScreen.styles';

export interface ErrorScreenProps {
  reason?: ErrorReason;
  title?: ReactNode;
  description?: ReactNode;
  backButtonLink?: string | false;
  backButtonTitle?: string;
}

export function ErrorScreen({
  reason: reasonFromProps = 'unknown',
  backButtonLink = '/',
  backButtonTitle,
  description: descriptionFromProps,
  title: titleFromProps,
}: ErrorScreenProps) {
  const error = useRouteError() as AppError | undefined;

  const errorCause = error?.cause as AppErrorCause;

  const { t } = useTranslation(namespaces.common);

  const reason = errorCause?.reason || reasonFromProps;
  const title = errorCause?.title || titleFromProps || t('errors.defaultTitle');
  const description = errorCause?.description || descriptionFromProps || t('errors.defaultDescription');

  backButtonTitle ||= t('errors.backButton');

  const renderGraphics = () => {
    switch (reason) {
      case 'not-authorized':
        return <Code401 role="presentation" aria-label="401 code" height="100%" />;

      case 'forbidden':
        return <Code403 role="presentation" aria-label="403 code" height="100%" />;

      case 'not-found':
        return <Code404 role="presentation" aria-label="404 code" height="100%" />;

      case 'unknown':
      default:
        return <Code500 role="presentation" aria-label="500 code" height="100%" />;
    }
  };

  return (
    <Container role="article" aria-label="error screen container" aria-details={reason}>
      <LogoBox>
        <SternumLogoWithTextIcon color={Colors.BrandBlueLightest} />
      </LogoBox>

      <Content>
        <CodeBox>{renderGraphics()}</CodeBox>
        <FlexBox flexDirection="column" zIndex={1}>
          {_.isString(title) ? <Title>{title}</Title> : title}
          {_.isString(description) ? <Description mt={8}>{description}</Description> : description}
          {backButtonLink !== false && (
            <Box mt={32}>
              <ThemeProvider mode="Dark">
                <Button as={Link} to={backButtonLink}>
                  {backButtonTitle}
                </Button>
              </ThemeProvider>
            </Box>
          )}
        </FlexBox>
      </Content>

      <LinesBox position="absolute" w="15vw" minWidth={250} left={0} top={0}>
        <TopLines width="100%" preserveAspectRatio="xMinYMin meet" />
      </LinesBox>
      <LinesBox position="absolute" w="30vw" minWidth={500} right={0} bottom={0}>
        <BottomLines width="100%" preserveAspectRatio="xMaxYMax meet" />
      </LinesBox>
    </Container>
  );
}
